import { create } from 'zustand';

/**
 * Who is typing, on which chat, right now.
 *
 * A store of its own rather than query state, because it is neither: nothing here is read from the
 * server, nothing is written to it, and the fact expires on its own. It is the one piece of state
 * in this workspace whose correct behaviour is to be **forgotten**.
 *
 * Every entry has a deadline. The widget says `typing: false` when the composer empties, and a
 * browser that is closed mid-sentence never says anything — so an indicator with no expiry would
 * sit on an agent's screen claiming somebody is still typing to them. The deadline is longer than
 * the widget's own four-second idle timer, so an honest `false` always arrives first.
 */
const expiryMs = 8_000;

interface TypingState {
  /** By topic: when the claim stops being true. */
  readonly until: Readonly<Record<string, number>>;
  readonly peerTyping: (topic: string, typing: boolean, now?: number) => void;
  readonly clear: (topic: string) => void;
}

export const useTypingStore = create<TypingState>((set) => ({
  until: {},
  peerTyping: (topic, typing, now = Date.now()) =>
    set((state) => {
      if (!typing) {
        const { [topic]: _gone, ...rest } = state.until;
        return { until: rest };
      }
      return { until: { ...state.until, [topic]: now + expiryMs } };
    }),
  clear: (topic) =>
    set((state) => {
      const { [topic]: _gone, ...rest } = state.until;
      return { until: rest };
    }),
}));

/**
 * Whether the other side of this chat is typing, as of `now`.
 *
 * `now` is a parameter so the caller decides how often the answer is re-evaluated: a pane ticks a
 * clock while it is open, and a pane nobody is looking at does not.
 */
export function isPeerTyping(topic: string | undefined, now: number): boolean {
  if (topic === undefined) {
    return false;
  }
  const until = useTypingStore.getState().until[topic];
  return until !== undefined && until > now;
}
